/**
 * Public guide article page.
 *
 * Only shows published articles. The backend enforces status + published_at,
 * but we keep UI defensive.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { applySeo } from '../utils/seo';
import { ArticleSchema } from '../components/StructuredData';
import { DEFAULT_OG_IMAGE } from '../utils/seoDefaults';
import { publicLoadErrorMessage } from '../lib/publicSiteErrorMessage';
import { isScaledWeeklyGuideSlug, robotsForGuideSlug } from '../utils/scaledGuide';

/** Unwrap JSON-wrapped content (e.g. {"content": "..."}) so markdown renders; convert literal \n to newlines; strip image-hint HTML comments from display. */
function normalizeGuideContent(content: string): string {
  if (!content || typeof content !== 'string') return '';
  const trimmed = content.trim();
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as { content?: string; contentMd?: string };
      const inner = parsed.content ?? parsed.contentMd ?? content;
      return String(inner).replace(/\\n/g, '\n').replace(/\\r/g, '\r');
    } catch {
      // not valid JSON, use as-is
    }
  }
  let out = content.replace(/\\n/g, '\n').replace(/\\r/g, '\r');
  // Strip image-hint comments so visitors don't see them (e.g. <!-- image: butter tarts -->)
  out = out.replace(/<!--[\s\S]*?-->/g, '').trim();
  return out;
}

/** Extract FAQ pairs from markdown: ## FAQ section with ### questions and following paragraph(s). */
function extractFaqFromMarkdown(md: string): { question: string; answer: string }[] {
  if (!md || typeof md !== 'string') return [];
  const faqSection = md.match(/##\s*FAQ[\s\S]*?(?=##\s|$)/i);
  if (!faqSection) return [];
  const section = faqSection[0];
  const blocks = section.split(/(?=###\s)/).filter(Boolean);
  const pairs: { question: string; answer: string }[] = [];
  for (const block of blocks) {
    const questionMatch = block.match(/^###\s+(.+?)(?:\n|$)/);
    if (!questionMatch) continue;
    const question = questionMatch[1].trim();
    const answerBlock = block.replace(/^###\s+.+?\n?/, '').trim();
    const answer = answerBlock.replace(/\n+/g, ' ').trim().slice(0, 500);
    if (question && answer) pairs.push({ question, answer });
  }
  return pairs;
}

type Guide = {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content_md: string;
  published_at: string | null;
};

// Related Products Section Component
function RelatedProductsSection() {
  const [products, setProducts] = useState<Array<{ id: string; name: string; price: number; image_url: string | null; category: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const API_URL = import.meta.env.VITE_API_URL || '';
        const url = API_URL ? `${API_URL}/api/menu?popular=true` : '/api/menu?popular=true';
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setProducts((data.items || []).slice(0, 3));
        }
      } catch (err) {
        console.error('Failed to fetch related products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  if (loading || products.length === 0) return null;

  return (
    <div className="mt-8 bg-white rounded-2xl border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">🔥 Popular Treats You Might Like</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {products.map((product) => (
          <Link
            key={product.id}
            to="/menu"
            className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all group"
          >
            {product.image_url ? (
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  loading="lazy"
                />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-2xl flex-shrink-0">
                🧁
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 text-sm line-clamp-1">{product.name}</h4>
              <p className="text-primary-600 font-bold text-sm">${product.price.toFixed(2)}</p>
            </div>
          </Link>
        ))}
      </div>
      <div className="text-center mt-4">
        <Link
          to="/menu"
          className="text-purple-700 hover:text-purple-800 font-semibold text-sm"
        >
          View Full Menu →
        </Link>
      </div>
    </div>
  );
}

export default function GuideArticle() {
  const { slug } = useParams();

  const [guide, setGuide] = useState<Guide | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/articles/${encodeURIComponent(String(slug || ''))}`);
        if (res.status === 404) {
          setGuide(null);
          return;
        }
        if (!res.ok) {
          console.error('Guide article fetch failed:', res.status);
          setError(publicLoadErrorMessage());
          return;
        }
        const data = await res.json();
        setGuide(data.article || null);
      } catch (e) {
        console.error('Guide article load:', e);
        setError(publicLoadErrorMessage());
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  useEffect(() => {
    if (!guide) return;
    applySeo({
      title: `${guide.title} | Nanny B's Bakery`,
      description:
        guide.excerpt ||
        `Read our guide on ${guide.title} from Nanny B's Bakery in Barrie, Ontario. Tips for custom cakes, ordering, and celebration planning.`,
      canonicalPath: `/guides/${guide.slug}`,
      keywords: 'bakery Barrie, custom cakes Barrie, Nanny B bakery guide',
      robots: robotsForGuideSlug(guide.slug),
      og: {
        type: 'article',
        title: `${guide.title} | Nanny B's Bakery`,
        description:
          guide.excerpt ||
          `Read our guide on ${guide.title} from Nanny B's Bakery in Barrie, Ontario.`,
        image: DEFAULT_OG_IMAGE,
      },
    });
  }, [guide]);

  const faqItems = useMemo(() => {
    if (!guide?.content_md) return [];
    const normalized = normalizeGuideContent(guide.content_md);
    return extractFaqFromMarkdown(normalized);
  }, [guide?.content_md]);

  const faqSchema = useMemo(() => {
    if (faqItems.length === 0) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map(({ question, answer }) => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: { '@type': 'Answer', text: answer },
      })),
    };
  }, [faqItems]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="mb-6">
        <Link to="/guides" className="text-purple-700 hover:text-purple-800 font-medium">
          ← Back to Guides
        </Link>
      </div>

      {loading && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 text-gray-600">
          Loading guide...
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-50 rounded-2xl border border-red-200 p-6 text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && !guide && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 text-gray-600">
          Guide not found.
        </div>
      )}

      {!loading && !error && guide && (
        <>
          {!isScaledWeeklyGuideSlug(guide.slug) && (
            <ArticleSchema
              title={guide.title}
              description={guide.excerpt || undefined}
              slug={guide.slug}
              publishedAt={guide.published_at}
            />
          )}
          {faqSchema && !isScaledWeeklyGuideSlug(guide.slug) && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
            />
          )}
          <header className="mb-6">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">{guide.title}</h1>
            {guide.excerpt && <p className="text-gray-600 mt-3">{guide.excerpt}</p>}
          </header>

          <article className="bg-white rounded-2xl border border-gray-200 p-6">
            <MarkdownRenderer markdown={normalizeGuideContent(guide.content_md)} />
          </article>

          {/* Special Offer for Guide Readers */}
          <div className="mt-8 bg-gradient-to-br from-amber-50 via-orange-50 to-pink-50 rounded-2xl p-6 border-2 border-amber-200">
            <div className="flex items-start gap-3 mb-4">
              <span className="text-3xl">🎁</span>
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-xs font-bold mb-2">
                  SPECIAL OFFER
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  Love This Guide? Try Our Treats!
                </h3>
                <p className="text-gray-700 text-sm">
                  Mention you found us through our guides and get <strong className="text-amber-700">10% off your first order</strong> or a <strong className="text-amber-700">free cookie with any $15+ purchase</strong>!
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/menu"
                className="inline-flex justify-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
              >
                Browse Menu
              </Link>
              <Link
                to="/order"
                className="inline-flex justify-center px-6 py-3 bg-white text-purple-700 rounded-xl font-semibold border-2 border-purple-300 hover:bg-purple-50 transition-all"
              >
                Order Now
              </Link>
              <a
                href="tel:7055034966"
                className="inline-flex justify-center px-6 py-3 bg-white text-gray-700 rounded-xl font-semibold border-2 border-gray-300 hover:bg-gray-50 transition-all"
              >
                📞 Call Us
              </a>
            </div>
          </div>

          {/* Related Products */}
          <RelatedProductsSection />

          {/* Main CTA */}
          <div className="mt-8 bg-gradient-to-r from-purple-600 to-pink-500 rounded-2xl p-6 text-white">
            <h3 className="text-xl font-semibold">Ready to order?</h3>
            <p className="text-purple-100 mt-1 mb-4">
              Browse the menu or place an order. We&apos;ll confirm pickup details with you.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/menu"
                className="inline-flex justify-center px-4 py-2 bg-white text-purple-700 rounded-xl font-semibold hover:bg-purple-50 transition-colors"
              >
                View Menu
              </Link>
              <Link
                to="/order"
                className="inline-flex justify-center px-4 py-2 bg-white/20 text-white rounded-xl font-semibold hover:bg-white/30 transition-colors"
              >
                Place an Order
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


